import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { getMarketData } from "../engine";
import { useReplay } from "./ReplayContext";

const MarketContext = createContext(null);
const HIST = 40;

// Quotes for watchlist / strip / tickets — all fed by the shared Market Data Engine tick bus.
export function MarketProvider({ children }) {
  const [quotes, setQuotes] = useState({});
  const [feed, setFeed] = useState({ connection: "connecting", mode: "connecting" });
  const subsRef = useRef(new Map()); // code -> unsubscribe
  const quotesRef = useRef({});

  useEffect(() => getMarketData().onStatus(setFeed), []);

  const pushTicks = useCallback((code, ticks) => {
    if (!ticks.length) return;
    const q = quotesRef.current[code] || { first: null, history: [] };
    const first = q.first ?? ticks[0].price;
    const history = [...q.history, ...ticks.map((t) => t.price)].slice(-HIST);
    const price = ticks[ticks.length - 1].price;
    const next = { price, at: Date.now(), prev: q.price ?? price, first, change: price - first, changePct: first ? ((price - first) / first) * 100 : 0, history };
    quotesRef.current = { ...quotesRef.current, [code]: next };
    setQuotes((prev) => ({ ...prev, [code]: next }));
  }, []);

  const mainRef = useRef([]);
  const extraRef = useRef([]);
  const sync = useCallback((codes) => {
    const md = getMarketData();
    const want = new Set(codes);
    for (const [code, off] of subsRef.current.entries()) {
      if (!want.has(code)) { off(); subsRef.current.delete(code); }
    }
    for (const code of codes) {
      if (subsRef.current.has(code)) continue;
      subsRef.current.set(code, md.subscribe(code, (ticks, initial) => {
        if (initial) { quotesRef.current = { ...quotesRef.current, [code]: undefined }; }
        pushTicks(code, ticks);
      }, 60));
    }
  }, [pushTicks]);
  // Primary set (favourites, strip, chart symbol) + transient extras (visible watchlist tab) share one tick bus.
  const subscribeSymbols = useCallback((codes) => { mainRef.current = codes; sync([...new Set([...codes, ...extraRef.current])]); }, [sync]);
  const subscribeExtra = useCallback((codes) => { extraRef.current = codes; sync([...new Set([...mainRef.current, ...codes])]); }, [sync]);

  // Re-route the replayed symbol's quote through the replay feed (and back to live afterwards).
  const { sessionKey, replay } = useReplay();
  const prevReplaySymbol = useRef(null);
  useEffect(() => {
    const code = replay.symbol || prevReplaySymbol.current;
    if (!code || !subsRef.current.has(code)) { prevReplaySymbol.current = replay.symbol; return; }
    subsRef.current.get(code)();
    subsRef.current.delete(code);
    sync([...subsRef.current.keys(), code]);
    prevReplaySymbol.current = replay.active ? replay.symbol : null;
  }, [sessionKey]); // eslint-disable-line

  const status = feed.connection;
  return (
    <MarketContext.Provider value={{ quotes, status, feed: replay.active ? { ...feed, mode: "replay" } : feed, subscribeSymbols, subscribeExtra }}>
      {children}
    </MarketContext.Provider>
  );
}

export const useMarket = () => useContext(MarketContext);
